import { z } from 'zod';

import type {
  CreateStoreRequest,
  CreateStoreResponse,
  GetStoreRequest,
  GetStoreResponse,
  ListStoresByCompanyRequest,
  ListStoresByCompanyResponse,
  StoreDto,
  UpdateStoreRequest,
  UpdateStoreResponse,
} from './store.dto.js';

const timestampSchema = z.iso.datetime();
const timeZoneSchema = z.string().trim().min(1).refine(isValidIanaTimeZone, { message: 'Invalid time zone' });

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

const storeMetadataSchema = z.object({
  phone: z.string().trim().min(1).optional(),
  contactEmail: z.string().trim().pipe(z.email()).optional(),
  notes: z.string().trim().min(1).optional(),
});

export const StoreSchema: z.ZodType<StoreDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  name: z.string().trim().min(1),
  code: z.string().trim().min(1).optional(),
  timezone: timeZoneSchema.optional(),
  mainLocationId: z.uuid().optional(),
  operatingModel: z.enum(['mono_station', 'multi_station'] as const),
  primaryStationId: z.uuid().optional(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
}).extend(storeMetadataSchema.shape);

export const CreateStoreRequestSchema: z.ZodType<CreateStoreRequest> = z.object({
  companyId: z.uuid(),
  name: z.string().trim().min(1),
  code: z.string().trim().min(1).optional(),
  timezone: timeZoneSchema.optional(),
  mainLocationId: z.uuid().optional(),
  operatingModel: z.enum(['mono_station', 'multi_station'] as const).optional(),
  primaryStationId: z.uuid().optional(),
}).extend(storeMetadataSchema.shape);

export const CreateStoreResponseSchema: z.ZodType<CreateStoreResponse> = z.object({
  store: StoreSchema,
});

export const UpdateStoreRequestSchema: z.ZodType<UpdateStoreRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    name: z.string().trim().min(1).optional(),
    code: z.string().trim().min(1).optional(),
    timezone: timeZoneSchema.nullable().optional(),
    mainLocationId: z.uuid().optional(),
    operatingModel: z.enum(['mono_station', 'multi_station'] as const).optional(),
    primaryStationId: z.uuid().nullable().optional(),
  })
  .extend(storeMetadataSchema.shape)
  .refine(
    (value) =>
      value.name !== undefined ||
      value.code !== undefined ||
      value.timezone !== undefined ||
      value.mainLocationId !== undefined ||
      value.operatingModel !== undefined ||
      value.primaryStationId !== undefined ||
      value.phone !== undefined ||
      value.contactEmail !== undefined ||
      value.notes !== undefined,
    { message: 'At least one field must be provided to update store' },
  );

export const UpdateStoreResponseSchema: z.ZodType<UpdateStoreResponse> = z.object({
  store: StoreSchema,
});

export const GetStoreRequestSchema: z.ZodType<GetStoreRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
});

export const GetStoreResponseSchema: z.ZodType<GetStoreResponse> = z.object({
  store: StoreSchema,
});

export const ListStoresByCompanyRequestSchema: z.ZodType<ListStoresByCompanyRequest> = z.object({
  companyId: z.uuid(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});

export const ListStoresByCompanyResponseSchema: z.ZodType<ListStoresByCompanyResponse> = z.object({
  items: z.array(StoreSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(200),
  total: z.number().int().min(0),
});
